import { useMemo, useRef, useEffect } from 'react'
import { WEEKDAYS, WEEKDAY_COLORS, colorFor } from './weekColors'

const PAD_FACTOR = 0.15

// Web-mercator-ish projection (good enough at our scale; for production-grade
// accuracy use a real mercator helper). Returns { x, y } in canvas pixels.
function projectLatLng (lat, lng, bounds, w, h) {
  const lngSpan = bounds.maxLng - bounds.minLng
  const latSpan = bounds.maxLat - bounds.minLat
  const x = ((lng - bounds.minLng) / lngSpan) * w
  const y = h - ((lat - bounds.minLat) / latSpan) * h
  return { x, y }
}

function computeBounds (stops, yard) {
  const lats = stops.map(s => s.lat).concat(yard ? [yard.lat] : [])
  const lngs = stops.map(s => s.lng).concat(yard ? [yard.lng] : [])
  if (!lats.length) return null
  let minLat = Math.min(...lats), maxLat = Math.max(...lats)
  let minLng = Math.min(...lngs), maxLng = Math.max(...lngs)
  const latPad = (maxLat - minLat) * PAD_FACTOR || 0.01
  const lngPad = (maxLng - minLng) * PAD_FACTOR || 0.01
  return {
    minLat: minLat - latPad, maxLat: maxLat + latPad,
    minLng: minLng - lngPad, maxLng: maxLng + lngPad
  }
}

// Walk a route's coordinates (GeoJSON [lng,lat] pairs) into screen-space points.
// Falls back to yard→stops→yard if no geometry is attached to the day group.
function routeCoords (group, yard) {
  const coords = group.geometry?.geometry?.coordinates
  if (coords && coords.length) return coords.map(([lng, lat]) => ({ lat, lng }))
  const pts = []
  if (yard) pts.push({ lat: yard.lat, lng: yard.lng })
  for (const s of group.stops || []) pts.push({ lat: s.lat, lng: s.lng })
  if (yard) pts.push({ lat: yard.lat, lng: yard.lng })
  return pts
}

function DayTile ({ dayIndex, groups, yard, bounds, colorMode, crewColors }) {
  const canvasRef = useRef(null)
  const borderColor = WEEKDAY_COLORS[dayIndex]
  const totalStops = useMemo(
    () => groups.reduce((a, g) => a + (g.stops?.length || 0), 0),
    [groups]
  )

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !bounds) return
    const w = canvas.width = canvas.clientWidth
    const h = canvas.height = canvas.clientHeight
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, w, h)

    // Background: faint grid
    ctx.strokeStyle = 'rgba(0,0,0,0.06)'
    ctx.lineWidth = 1
    for (let i = 0; i < 8; i++) {
      const x = (i / 8) * w
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke()
      const y = (i / 8) * h
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke()
    }

    if (!groups.length) return

    // One polyline per crew-day group, colored per scope's colorMode.
    for (const g of groups) {
      const color = colorFor({ dayIndex: g.dayIndex, crewId: g.crewId }, colorMode, crewColors)
      const pts = routeCoords(g, yard)
      if (pts.length > 1) {
        ctx.strokeStyle = color
        ctx.lineWidth = 2
        ctx.lineJoin = 'round'
        ctx.lineCap = 'round'
        ctx.beginPath()
        const first = projectLatLng(pts[0].lat, pts[0].lng, bounds, w, h)
        ctx.moveTo(first.x, first.y)
        for (let i = 1; i < pts.length; i++) {
          const p = projectLatLng(pts[i].lat, pts[i].lng, bounds, w, h)
          ctx.lineTo(p.x, p.y)
        }
        ctx.stroke()
      }

      // Stop markers, colored per the same colorMode.
      for (const s of g.stops || []) {
        const p = projectLatLng(s.lat, s.lng, bounds, w, h)
        ctx.beginPath()
        ctx.arc(p.x, p.y, 4.5, 0, 2 * Math.PI)
        ctx.fillStyle = color
        ctx.fill()
        ctx.strokeStyle = '#ffffff'
        ctx.lineWidth = 1.5
        ctx.stroke()
      }
    }

    // Yard marker (dark, drawn last so it sits on top of routes)
    if (yard) {
      const yp = projectLatLng(yard.lat, yard.lng, bounds, w, h)
      ctx.beginPath()
      ctx.arc(yp.x, yp.y, 6, 0, 2 * Math.PI)
      ctx.fillStyle = '#1c2419'
      ctx.fill()
      ctx.fillStyle = '#ffffff'
      ctx.font = 'bold 9px sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText('Y', yp.x, yp.y + 3)
    }
  }, [groups, yard, bounds, colorMode, crewColors])

  return (
    <div style={{
      position: 'relative', border: `2px solid ${borderColor}`, borderRadius: 10,
      overflow: 'hidden', minHeight: 0, minWidth: 0,
      background: 'linear-gradient(135deg, #d6c9a8, #b5a47e)'
    }}>
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
      <div style={{
        position: 'absolute', top: 6, left: 6, display: 'flex', alignItems: 'center',
        gap: 5, background: 'rgba(251,248,241,0.94)', borderRadius: 999,
        padding: '3px 8px', boxShadow: '0 2px 6px rgba(0,0,0,0.18)'
      }}>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: borderColor }} />
        <span style={{ fontSize: 11, fontWeight: 800, color: '#1c2419' }}>{WEEKDAYS[dayIndex]?.full}</span>
        <span style={{ fontSize: 10.5, fontWeight: 600, color: '#97a094' }}>{totalStops}</span>
      </div>
    </div>
  )
}

export default function WeekGridView ({ dayGroups, yard, colorMode = 'day', crewColors = {} }) {
  // Auto-fit bounds across ALL visible stops + yard
  const allStops = useMemo(() => dayGroups.flatMap(g => g.stops || []), [dayGroups])
  const bounds = useMemo(() => computeBounds(allStops, yard), [allStops, yard])

  // Keep groups separate per dayIndex so each crew renders in its own color.
  const groupsByDay = useMemo(() => {
    const m = new Map()
    for (let i = 0; i < 5; i++) m.set(i, [])
    for (const g of dayGroups) {
      if (!m.has(g.dayIndex)) m.set(g.dayIndex, [])
      m.get(g.dayIndex).push(g)
    }
    return m
  }, [dayGroups])

  return (
    <div style={{
      position: 'absolute', inset: 0, padding: 8, background: '#efe9da',
      overflowY: 'auto', overflowX: 'hidden', zIndex: 500,
      overscrollBehavior: 'contain'
    }}>
      <div style={{
        minHeight: 'max(100%, 720px)',
        display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gridTemplateRows: 'repeat(2, minmax(320px, 1fr))', gap: 8
      }}>
        {[0, 1, 2, 3, 4].map(i => (
          <DayTile
            key={i}
            dayIndex={i}
            groups={groupsByDay.get(i) || []}
            yard={yard}
            bounds={bounds}
            colorMode={colorMode}
            crewColors={crewColors}
          />
        ))}
        <div style={{
          borderRadius: 10, border: '1.5px dashed #cec3a5', minHeight: 0, minWidth: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 4, color: '#8a8472', padding: 10, textAlign: 'center'
        }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: '#6f7a6b' }}>Same area, different days?</div>
          <div style={{ fontSize: 10.5, maxWidth: 160, lineHeight: 1.4 }}>
            Compare the five maps — clusters that repeat are territory candidates.
          </div>
        </div>
      </div>
    </div>
  )
}
